import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  MdFilterListOff,
  MdRefresh,
  MdTimeline,
} from 'react-icons/md';
import { Page } from '@components/layout/Page';
import { PageEmpty } from '@components/layout/PageEmpty';
import { DataPagination } from '@components/ui/DataPagination';
import { GridSkeleton } from '@components/ui/GridSkeleton';
import { ForecastRunDetailDialog } from '@features/forecastRuns/components/ForecastRunDetailDialog';
import { ForecastRunsGrid } from '@features/forecastRuns/components/ForecastRunsGrid';
import { useForecastRuns } from '@features/forecastRuns/hooks';
import {
  FORECAST_RUN_METRICS,
  FORECAST_RUN_STATUSES,
  type ForecastRun,
  type ForecastRunMetric,
  type ForecastRunSortKey,
  type ForecastRunStatus,
  type ListForecastRunsParams,
  type SortDirection,
} from '@features/forecastRuns/schema';

const DEFAULT_PAGE_SIZE = 50;

export default function ForecastRunsScreen() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [status, setStatus] = useState<'' | ForecastRunStatus>('');
  const [metric, setMetric] = useState<'' | ForecastRunMetric>('');
  const [sort, setSort] = useState<{
    sortBy?: ForecastRunSortKey;
    sortDir?: SortDirection;
  }>({});

  const [detailRow, setDetailRow] = useState<ForecastRun | null>(null);

  useEffect(() => {
    setPage(1);
  }, [status, metric, pageSize, sort]);

  const params = useMemo<ListForecastRunsParams>(
    () => ({
      page,
      pageSize,
      status: status || undefined,
      metric: metric || undefined,
      sortBy: sort.sortBy,
      sortDir: sort.sortDir,
    }),
    [page, pageSize, status, metric, sort],
  );

  const { data, isLoading, isFetching, refetch } = useForecastRuns(params);

  const totalRecords = data?.paginationDetails?.totalRecords ?? 0;
  const hasActiveFilters = Boolean(status || metric);
  const isInitialLoad = isLoading && !data;
  const showEmpty = !isLoading && (data?.rowData?.length ?? 0) === 0;

  const clearFilters = () => {
    setStatus('');
    setMetric('');
  };

  return (
    <Page
      title="Forecast runs"
      subtitle="Pipeline executions — status, timing, row counts, errors."
      fullHeight
    >
      <div className="mb-3 flex flex-col gap-3 rounded-xl bg-surface-muted px-3 py-3 md:flex-row md:flex-wrap md:items-end md:px-4">
        <TextField
          label="Status"
          size="small"
          select
          value={status}
          onChange={(e) => setStatus(e.target.value as '' | ForecastRunStatus)}
          className="w-full md:w-[160px]"
        >
          <MenuItem value="">All</MenuItem>
          {FORECAST_RUN_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Metric"
          size="small"
          select
          value={metric}
          onChange={(e) => setMetric(e.target.value as '' | ForecastRunMetric)}
          className="w-full md:w-[130px]"
        >
          <MenuItem value="">All</MenuItem>
          {FORECAST_RUN_METRICS.map((m) => (
            <MenuItem key={m} value={m}>
              {m === 'both' ? 'Both' : m === 'max' ? 'Max' : 'Modal'}
            </MenuItem>
          ))}
        </TextField>
        <div className="flex items-end gap-2 md:contents">
          <TextField
            label="Page size"
            size="small"
            select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="flex-1 md:w-[110px] md:flex-initial"
          >
            {[25, 50, 100].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>

          <div className="flex items-center gap-2 md:ml-auto">
            {hasActiveFilters && (
              <Button variant="text" size="small" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
            <Tooltip title={isFetching ? 'Loading…' : 'Refresh'}>
              <span>
                <IconButton onClick={() => refetch()} size="small" disabled={isFetching}>
                  {isFetching ? <CircularProgress size={16} thickness={5} /> : <MdRefresh />}
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 overflow-hidden rounded-xl border border-border bg-surface">
        {isFetching && data && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 5,
              height: 2,
            }}
          />
        )}
        {isInitialLoad ? (
          <GridSkeleton
            rows={6}
            columns={[
              { width: '90px', bar: 'w-10' },
              { width: '130px', bar: 'w-16' },
              { width: '110px', bar: 'w-14' },
              { width: '200px', bar: 'w-3/4' },
              { width: '220px', bar: 'w-3/4' },
              { width: '130px', bar: 'w-16' },
              { width: '240px', bar: 'w-1/2' },
              { width: '1fr', empty: true },
            ]}
          />
        ) : showEmpty ? (
          hasActiveFilters ? (
            <PageEmpty
              icon={MdFilterListOff}
              title="No matches"
              message="No runs match the current filters."
              action={
                <Button variant="outlined" size="small" onClick={clearFilters}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <PageEmpty
              icon={MdTimeline}
              title="No pipeline runs yet"
              message="Forecast pipeline executions will appear here once the external job starts writing to etl.forecast_run."
            />
          )
        ) : (
          <ForecastRunsGrid
            data={data}
            onRowClick={setDetailRow}
            onSortChange={setSort}
          />
        )}
      </div>

      <DataPagination
        page={page}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={setPage}
      />

      <ForecastRunDetailDialog
        open={!!detailRow}
        row={detailRow}
        onClose={() => setDetailRow(null)}
      />
    </Page>
  );
}
